import React, { Fragment, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import { STATUS_COLORS } from "../../../enums/statusEnums";
import { PAGE_OPTIONS } from "../../../enums/dataGridEnums";
import { CheckCircle, MagnifyingGlass, Prohibit, Trash, X } from "@phosphor-icons/react";
import { useFormik } from "formik";
import { GenericAvatar } from "../../../components/generic/GenericAvatar";
import { banReportedUser, dismissReport, messageReportedUser } from "../../../config/apisauce";
import useNotiStack from "../../../hooks/useNotiStack";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { object, string } from "yup";
import { MAX_CHARS } from "../../../enums/generalEnums";
import CustomDataGrid, { CustomNoResultsOverlay } from "../../../components/Table/CustomDataGrid";
import createReportedUsersQueryOptions from "../../../queryOptions/createReportedUsersQueryOptions";
import useDebounce from "react-use/esm/useDebounce";
import { formatDate } from "../../../helpers/dateHelper";
import { DAY_ONLY_FORMAT } from "../../../enums/dateEnums";

export const ReportedCreatorsTable = () => {
  const [keyword, setKeyword] = useState("");
  const [tableState, setTableState] = useState({
    filter: {
      search: "",
    },
    sort: ["-created_at"],
    pageSize: 10,
    page: 1,
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  const { data: users, isFetching } = useQuery(
    createReportedUsersQueryOptions(tableState, { placeholderData: keepPreviousData })
  );

  const [mode, setMode] = useState("report-summary");

  const columns = useMemo(
    () => [
      {
        field: "reportable_name",
        headerName: "Reported Creator",
        minWidth: 200,
        flex: 1,
        editable: true,
        renderCell: ({ row }) => {
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1 }}>
              <GenericAvatar location={row.reportable.details?.profile_picture?.location} />
              <Typography fontWeight={700}>{row.reportable.name}</Typography>
            </Box>
          );
        },
      },
      {
        field: "reporter_user_name",
        headerName: "Reported By",
        minWidth: 200,
        flex: 1,
        editable: true,
        renderCell: ({ row }) => {
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1 }}>
              <GenericAvatar location={row.reporter.details?.profile_picture?.location} />
              <Typography fontWeight={700}>{row.reporter.name}</Typography>
            </Box>
          );
        },
      },
      {
        field: "created_at",
        headerName: "Date Submitted",
        minWidth: 120,
        flex: 1,
        valueGetter: (value, row) => {
          const date = new Date(row.created_at);
          return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          });
        },
      },
      {
        field: "status",
        headerName: "Status",
        sortable: false,
        minWidth: 120,
        flex: 1,
        renderCell: ({ row }) => {
          return (
            <Typography color={STATUS_COLORS[row.status]} mt={1.7} fontWeight={700} textTransform={"capitalize"}>
              {row.status}
            </Typography>
          );
        },
      },
      {
        field: "action_taken",
        headerName: "Action Taken",
        minWidth: 120,
        flex: 1,
        valueGetter: (value, row) => {
          if (row.action_taken === "banned") {
            return "Banned";
          } else if (row.action_taken === "dismissed") {
            return "Dismissed";
          } else if (row.action_taken === "messaged") {
            return "Message Sent";
          }
          return "None";
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        minWidth: 380,
        flex: 1,
        renderCell: ({ row }) => (
          <Stack direction="row" mt={1} gap={1}>
            <Button
              fullWidth
              size="large"
              component="a"
              href={`creator?user=${row.reportable.name}-${row.reportable.id}`}
              variant="outlined"
              sx={{ textDecoration: "none" }}
              target="_blank"
              rel="noopener noreferrer"
            >
              View Profile
            </Button>
            <Button fullWidth variant="contained" size="large" onClick={() => handleReview(row)}>
              Review
            </Button>
          </Stack>
        ),
      },
    ],
    []
  );

  const handleReview = (report) => {
    setSelectedReport(report);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedReport(null);
    setMode("report-summary");
  };

  const ModeComponentMap = {
    "report-summary": {
      title: "Report Summary",
      component: <ReportSummary reportedUser={selectedReport} handleCloseDialog={handleCloseDialog} setMode={setMode} />,
      width: "1024px",
    },
    "ban-action": {
      title: "Ban Confirm Action",
      component: <BanActionPanel reportedUser={selectedReport} handleCloseDialog={handleCloseDialog} setMode={setMode} />,
      width: "550px",
    },
    "profile-banned": {
      title: "Profile Banned",
      component: <ProfileBannedPanel reportedUser={selectedReport} handleCloseDialog={handleCloseDialog} />,
      width: "550px",
    },
    "dismiss-action": {
      title: "Dismiss Report",
      component: <DismissActionPanel reportedUser={selectedReport} setMode={setMode} />,
      width: "550px",
    },
    "report-dismissed": {
      title: "Report Dismissed",
      component: <ReportDismissedPanel handleCloseDialog={handleCloseDialog} />,
      width: "550px",
    },
    "message-action": {
      title: "Message Profile",
      component: <MessageActionPanel reportedUser={selectedReport} handleCloseDialog={handleCloseDialog} setMode={setMode} />,
      width: "550px",
    },
    "message-sent": {
      title: "Message Sent",
      component: <MessageSentPanel handleCloseDialog={handleCloseDialog} />,
      width: "550px",
    },
  };

  const [,] = useDebounce(
    () => {
      setTableState((prev) => ({
        ...prev,
        filter: {
          ...prev.filter,
          search: keyword,
        },
        page: 1,
      }));
    },
    1000,
    [keyword]
  );

  return (
    <Fragment>
      <Card>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xxs: "column", mds: "row" },
            justifyContent: "space-between",
            gap: 1,
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} color="primary">
              Reported Profile
            </Typography>
            <Typography fontWeight={500}>Review and take action on all reported WannaCollab creators.</Typography>
          </Box>
          <Box width={{ xxs: "100%", mds: "350px" }}>
            <FormControl fullWidth>
              <OutlinedInput
                fullWidth
                variant="outlined"
                placeholder="Search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton edge="end">
                      <MagnifyingGlass />
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>
          </Box>
        </Box>

        {/* MOBILE VERSION */}
        <Box sx={{ display: { xxs: "flex", xsm: "none" }, flexDirection: "column", width: "100%" }}>
          <CreatorCardListMobile creators={users} setTableState={setTableState} handleReview={handleReview} />
        </Box>

        {/* TABLE */}
        <Box height={"635px"} sx={{ display: { xxs: "none", xsm: "block" }, overflowX: "auto" }}>
          <CustomDataGrid
            columns={columns}
            pageSizeOptions={PAGE_OPTIONS}
            rows={users?.data ?? []}
            rowCount={users?.total ?? 0}
            paginationMode="server"
            paginationModel={{
              pageSize: users?.per_page ?? 10,
              page: typeof users?.current_page === "number" ? users.current_page - 1 : 0,
            }}
            onPaginationModelChange={({ page, pageSize }) => {
              setTableState((prev) => ({
                ...prev,
                page: pageSize !== prev.pageSize ? 1 : page + 1,
                pageSize,
              }));
            }}
            disableColumnFilter
            disableColumnMenu
            disableColumnSelector
            disableDensitySelector
            disableRowSelectionOnClick
            checkboxSelection={false}
            loading={!users || isFetching}
          />
        </Box>
      </Card>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="mds"
        slotProps={{
          paper: {
            sx: {
              width: ModeComponentMap[mode]?.width,
            },
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <DialogTitle sx={{ m: 0, p: 0 }}>
            <Typography
              sx={{
                fontFamily: "Inter",
                fontWeight: 700,
                fontSize: "24px",
                lineHeight: "100%",
                textAlign: "center",
                color: "primary.main",
              }}
            >
              {ModeComponentMap[mode]?.title || "Report Summary"}
            </Typography>
          </DialogTitle>
          <IconButton aria-label="close" onClick={handleCloseDialog} sx={{ color: (theme) => theme.palette.grey[500] }}>
            <X />
          </IconButton>
        </Box>

        {ModeComponentMap[mode]?.component ?? <Box p={3}>Invalid mode</Box>}
      </Dialog>
    </Fragment>
  );
};

const ReportedUserCard = ({ title, user }) => {
  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Typography
        sx={{
          fontFamily: "Inter",
          fontWeight: 700,
          fontSize: "16px",
          lineHeight: "27px",
          letterSpacing: 0,
        }}
      >
        {title}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            backgroundColor: "#f0f0f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
          }}
        >
          <GenericAvatar location={user?.details?.profile_picture?.location} />
        </Box>
        <Typography
          sx={{
            fontFamily: "Inter",
            fontWeight: 400,
            fontSize: "16px",
            lineHeight: "27px",
            letterSpacing: 0,
          }}
        >
          {user.name}
        </Typography>
      </Box>
    </Box>
  );
};

const ACTIONS = Object.freeze({
  "ban-action": {
    value: "ban-action",
    label: "Ban profile",
    description: "Permanently block the user from the platform. This action is permanent and cannot be undone unless appealed.",
  },
  "dismiss-action": {
    value: "dismiss-action",
    label: "Dismiss report",
    description: "Note: Dismiss this report without further action. This will mark the issue as resolved and close the case.",
  },
  "message-action": {
    value: "message-action",
    label: "Message reported profile",
    description: "Note: Send a message to the reported profile.",
  },
});

const ReportSummary = ({ reportedUser, handleCloseDialog, setMode }) => {
  const formik = useFormik({
    initialValues: { action: null },
    validationSchema: object({
      action: string().required("Action is required"),
    }),
    onSubmit: (values, { resetForm }) => {
      setMode(values.action);
      resetForm();
    },
  });

  return (
    <Fragment>
      <DialogContent>
        {reportedUser && (
          <Stack spacing={3}>
            <ReportedUserCard title="Reported Profile:" user={reportedUser.reportable} />
            <ReportedUserCard title="Reported By:" user={reportedUser.reporter} />

            <Box>
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 700,
                  fontSize: "16px",
                  lineHeight: "27px",
                  letterSpacing: "0%",
                }}
              >
                Provided Context:
              </Typography>
              <Typography
                sx={{
                  mt: 0,
                  fontFamily: "Inter",
                  fontWeight: 400,
                  fontSize: "16px",
                  lineHeight: "27px",
                  letterSpacing: "0%",
                }}
              >
                {reportedUser.context || "No context provided."}
              </Typography>
            </Box>

            <Box>
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 700,
                  fontSize: "16px",
                  lineHeight: "27px",
                  letterSpacing: "0%",
                }}
              >
                Date Submitted:
              </Typography>
              <Typography
                sx={{
                  mt: 0,
                  fontFamily: "Inter",
                  fontWeight: 400,
                  fontSize: "16px",
                  lineHeight: "27px",
                  letterSpacing: "0%",
                }}
              >
                {new Date(reportedUser.created_at).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </Typography>
            </Box>

            <Box>
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 700,
                  fontSize: "16px",
                  lineHeight: "27px",
                  letterSpacing: "0%",
                }}
              >
                Action:
              </Typography>

              <FormControl fullWidth margin="normal" sx={{ gap: 1, mt: 0 }}>
                <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
                  <Select
                    displayEmpty
                    name="action"
                    value={formik.values.action}
                    onChange={(event) => formik.setFieldValue("action", event.target.value)}
                    error={formik.touched.action && Boolean(formik.errors.action)}
                    sx={{
                      borderRadius: "10px",
                    }}
                  >
                    <MenuItem value={null} disabled>
                      Select an option
                    </MenuItem>
                    {Object.values(ACTIONS).map((action) => (
                      <MenuItem key={action.value} value={action.value}>
                        {action.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.action && formik.errors.action && (
                    <Typography color="error" variant="caption" sx={{ mt: 0.5, mx: "14px" }}>
                      {formik.errors.action}
                    </Typography>
                  )}
                </Box>
              </FormControl>
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 500,
                  fontSize: "10px",
                  lineHeight: "18px",
                  letterSpacing: "0px",
                }}
              >
                {ACTIONS[formik.values.action]?.description || "Select an action to see the description."}
              </Typography>
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCloseDialog}>Close</Button>
        <Button variant="contained" onClick={() => formik.handleSubmit()}>
          Submit
        </Button>
      </DialogActions>
    </Fragment>
  );
};

const BanActionPanel = ({ reportedUser, setMode }) => {
  const { error } = useNotiStack();
  const queryClient = useQueryClient();

  const handleBanProfile = async () => {
    const response = await banReportedUser(reportedUser.id);

    if (response.ok) {
      queryClient.invalidateQueries({ queryKey: ["reported-users"] });
      setMode("profile-banned");
    } else {
      error("Failed to ban profile. Please try again later.");
    }
  };

  return (
    <Stack justifyContent={"center"} alignItems="center" spacing={3} pt={2}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          bgcolor: "#DEF2FC",
          color: "primary.main",
          borderRadius: 50,
          height: 120,
          width: 120,
        }}
      >
        <Prohibit size={120} weight="thin" />
      </Box>

      <Typography
        sx={{
          fontFamily: "Inter",
          fontWeight: 400,
          fontSize: "16px",
          lineHeight: "27px",
          letterSpacing: "0%",
          textAlign: "center",
        }}
      >
        You are about to permanently ban this profile from the platform. Please confirm if you would like to proceed.
      </Typography>
      <Stack direction={"row"} width={"100%"} justifyContent={"center"} spacing={2}>
        <Button fullWidth variant="outlined" onClick={() => setMode("report-summary")}>
          Cancel
        </Button>
        <Button fullWidth variant="contained" color="error" onClick={handleBanProfile}>
          Ban Profile
        </Button>
      </Stack>
    </Stack>
  );
};

const ProfileBannedPanel = ({ reportedUser, handleCloseDialog }) => {
  return (
    <Stack justifyContent={"center"} alignItems="center" spacing={3} pt={2}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          bgcolor: "#DEF2FC",
          color: "primary.main",
          borderRadius: 50,
          height: 120,
          width: 120,
        }}
      >
        <CheckCircle size={120} weight="thin" />
      </Box>

      <Typography
        sx={{
          fontFamily: "Inter",
          fontWeight: 400,
          fontSize: "16px",
          lineHeight: "27px",
          letterSpacing: "0%",
          textAlign: "center",
        }}
      >
        The profile&nbsp;
        <Typography
          variant="span"
          sx={{
            fontFamily: "Inter",
            fontWeight: 700,
            fontSize: "16px",
            lineHeight: "27px",
            letterSpacing: "0%",
            textAlign: "center",
          }}
        >
          {reportedUser.reportable.name}
        </Typography>
        &nbsp;has been successfully banned.
      </Typography>
      <Button variant="contained" color="primary" sx={{ width: "250px" }} onClick={handleCloseDialog}>
        close
      </Button>
    </Stack>
  );
};

const DismissActionPanel = ({ reportedUser, setMode }) => {
  const { error } = useNotiStack();
  const queryClient = useQueryClient();

  const handleDismissReport = async () => {
    const response = await dismissReport(reportedUser.id);

    if (response.ok) {
      queryClient.invalidateQueries({ queryKey: ["reported-users"] });
      setMode("report-dismissed");
    } else {
      error("Failed to dismiss report. Please try again later.");
    }
  };

  return (
    <Stack justifyContent={"center"} alignItems="center" spacing={3} pt={2}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          bgcolor: "#DEF2FC",
          color: "primary.main",
          borderRadius: 50,
          height: 120,
          width: 120,
        }}
      >
        <Trash size={120} weight="thin" />
      </Box>

      <Typography
        sx={{
          fontFamily: "Inter",
          fontWeight: 400,
          fontSize: "16px",
          lineHeight: "27px",
          letterSpacing: "0%",
          textAlign: "center",
        }}
      >
        You are about to dismiss this report. Please confirm if you would like to proceed.
      </Typography>
      <Stack direction={"row"} width={"100%"} justifyContent={"center"} spacing={2}>
        <Button fullWidth variant="outlined" onClick={() => setMode("report-summary")}>
          Cancel
        </Button>
        <Button fullWidth variant="contained" color="primary" onClick={handleDismissReport}>
          Dismiss Report
        </Button>
      </Stack>
    </Stack>
  );
};

const ReportDismissedPanel = ({ handleCloseDialog }) => {
  return (
    <Stack justifyContent={"center"} alignItems="center" spacing={3} pt={2}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          bgcolor: "#DEF2FC",
          color: "primary.main",
          borderRadius: 50,
          height: 120,
          width: 120,
        }}
      >
        <CheckCircle size={120} weight="thin" />
      </Box>

      <Typography
        sx={{
          fontFamily: "Inter",
          fontWeight: 400,
          fontSize: "16px",
          lineHeight: "27px",
          letterSpacing: "0%",
          textAlign: "center",
        }}
      >
        The report has been successfully dismissed.
      </Typography>
      <Button variant="contained" color="primary" sx={{ width: "250px" }} onClick={handleCloseDialog}>
        close
      </Button>
    </Stack>
  );
};

const validationSchema = object({
  message: string().required("Message is required").max(MAX_CHARS),
});

const MessageActionPanel = ({ reportedUser, setMode }) => {
  const { error } = useNotiStack();
  const queryClient = useQueryClient();

  const formik = useFormik({
    initialValues: { message: "" },
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      handleSendMessage(values, resetForm);
    },
  });

  const handleSendMessage = async () => {
    const response = await messageReportedUser(reportedUser.id, formik.values);

    if (response.ok) {
      queryClient.invalidateQueries({ queryKey: ["reported-users"] });
      setMode("message-sent");
    } else {
      error("Failed to send message. Please try again later.");
    }
  };

  return (
    <Stack component="form" onSubmit={formik.handleSubmit} justifyContent={"center"} alignItems="center" spacing={3} pt={2}>
      <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography color="textSecondary" variant="body1" fontWeight={700}>
          Enter Your Message <span style={{ color: "#F96D82" }}>*</span>
        </Typography>

        <TextField
          name="context"
          rows={6}
          fullWidth
          multiline
          value={formik.values.message}
          onChange={(e) => {
            if (e.target.value.length <= MAX_CHARS) {
              formik.setFieldValue("message", e.target.value);
            }
          }}
          onBlur={formik.handleBlur}
          error={formik.touched.message && Boolean(formik.errors.message)}
          helperText={formik.touched.message && formik.errors.message}
        />

        <Typography variant="caption" color={formik.values.message.length >= MAX_CHARS - 50 ? "error" : "textSecondary"}>
          {`${MAX_CHARS - formik.values.message.length} characters remaining`}
        </Typography>
      </Box>
      <Stack direction={"row"} width={"100%"} justifyContent={"center"} spacing={2}>
        <Button fullWidth variant="outlined" onClick={() => setMode("report-summary")}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" fullWidth>
          Send Message
        </Button>
      </Stack>
    </Stack>
  );
};

const MessageSentPanel = ({ handleCloseDialog }) => {
  return (
    <Stack justifyContent={"center"} alignItems="center" spacing={3} pt={2}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          bgcolor: "#DEF2FC",
          color: "primary.main",
          borderRadius: 50,
          height: 120,
          width: 120,
        }}
      >
        <CheckCircle size={120} weight="thin" />
      </Box>

      <Typography
        sx={{
          fontFamily: "Inter",
          fontWeight: 400,
          fontSize: "16px",
          lineHeight: "27px",
          letterSpacing: "0%",
          textAlign: "center",
        }}
      >
        Your message has been successfully sent.
      </Typography>
      <Button variant="contained" color="primary" sx={{ width: "250px" }} onClick={handleCloseDialog}>
        close
      </Button>
    </Stack>
  );
};

const CreatorCardListMobile = ({ creators, setTableState, handleReview }) => {
  const creatorList = useMemo(() => creators?.data || [], [creators]);

  if (!creatorList.length) {
    return (
      <Box py={2} textAlign="center">
        <CustomNoResultsOverlay />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          height: "670px",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {creatorList.map((creator, index) => (
          <Box key={`creator-list-card-${index}`}>
            <ReportCardMobile report={creator} handleReview={handleReview} />
            {index !== creatorList.length - 1 && <Divider sx={{ borderColor: "borders.main" }} />}
          </Box>
        ))}
      </Box>

      <Box>
        <TablePagination
          labelRowsPerPage="Per page:"
          component="div"
          count={creators.total}
          page={creators.current_page - 1}
          rowsPerPageOptions={PAGE_OPTIONS}
          onPageChange={(event, newPage) =>
            setTableState((prev) => ({
              ...prev,
              page: newPage + 1,
            }))
          }
          rowsPerPage={creators.per_page}
          onRowsPerPageChange={(event) =>
            setTableState((prev) => ({
              ...prev,
              pageSize: parseInt(event.target.value, 10),
              page: 1,
            }))
          }
        />
      </Box>
    </Box>
  );
};

const ReportCardMobile = ({ report, handleReview }) => {
  return (
    <Box sx={{ padding: 2, pr: 1 }}>
      <Box sx={{ display: "flex", flexDirection: "row", gap: 1.5 }}>
        <GenericAvatar
          location={report?.details?.profile_picture?.location}
          size={"92px"}
          backgroundColor={"background.default"}
        />
        <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
          <Stack direction={"row"} justifyContent={"space-between"}>
            <Typography fontWeight={700} noWrap width={{ xs: 140, md: "100%" }}>
              {report?.reportable?.name}
            </Typography>
            <Typography color="text.light">{formatDate(new Date(report?.created_at), DAY_ONLY_FORMAT)}</Typography>
          </Stack>
          <Typography noWrap>{report?.details?.profession ?? "None"}</Typography>
          <Typography fontWeight={700} textTransform={"capitalize"} color={STATUS_COLORS[report?.status]}>
            {report?.status}
          </Typography>
        </Box>
      </Box>

      <Stack direction={"column"} gap={1} mt={1.5}>
        <Button
          fullWidth
          component="a"
          href={`creator?user=${report?.reportable?.name}-${report.reportable.id}`}
          variant="outlined"
          sx={{ textDecoration: "none" }}
          target="_blank"
          rel="noopener noreferrer"
        >
          View Profile
        </Button>
        <Button fullWidth variant="contained" onClick={() => handleReview(report)}>
          Review
        </Button>
      </Stack>
    </Box>
  );
};
